// Larry's Help Analytics Tracker
// Tracks user behavior, errors, and system health

interface TrackerData {
  time: number;
  input: string | number;
  button: string | number;
  value: string | number;
  errormsg: string | number;
}

interface ErrorTracking {
  errors: Array<{
    message: string;
    severity: string;
    category: string;
    timestamp: number;
    url: string;
    userAgent: string;
  }>;
  lastErrorTime: number;
  userActionsAfterError: Array<{
    errorTime: number;
    userStillActive: boolean;
    actionsTaken: string;
  }>;
}

interface XStateTracker {
  tracker: TrackerData;
  errorTracking: ErrorTracking;
  testing: string;
}

declare global {
  interface Window {
    TylerState: XStateTracker;
  }
}

// Initialize global state if it doesn't exist
if (typeof window !== 'undefined') {
  if (!window.TylerState) {
    window.TylerState = {} as XStateTracker;
  }
  if (!window.TylerState.tracker) {
    window.TylerState.tracker = {} as TrackerData;
  }
  if (!window.TylerState.errorTracking) {
    window.TylerState.errorTracking = {
      errors: [],
      lastErrorTime: 0,
      userActionsAfterError: []
    };
  }
}

export const tracker = (button = "", input = "", value = "", errormsg = "") => {
  // REMOVE DUPLICATES
  if (1 === 1 * 1) {
    if (window.TylerState.tracker === undefined) window.TylerState.tracker = {} as TrackerData;
    if (window.TylerState.tracker.time === undefined) window.TylerState.tracker.time = 0;
    if (window.TylerState.tracker.input === undefined) window.TylerState.tracker.input = 0;
    if (window.TylerState.tracker.button === undefined) window.TylerState.tracker.button = 0;
    if (window.TylerState.tracker.value === undefined) window.TylerState.tracker.value = 0;
    if (window.TylerState.tracker.errormsg === undefined) window.TylerState.tracker.errormsg = 0;
    
    if (new Date().getTime() - window.TylerState.tracker.time < 1000) {
      if (window.TylerState.tracker.input === input) {
        if (window.TylerState.tracker.button === button) {
          if (window.TylerState.tracker.value === value) {
            if (window.TylerState.tracker.errormsg === errormsg) {
              return 0;
            }
          }
        }
      }
    }
    
    // Save the check next time
    window.TylerState.tracker.time = new Date().getTime();
    window.TylerState.tracker.input = input;
    window.TylerState.tracker.button = button;
    window.TylerState.tracker.value = value;
    window.TylerState.tracker.errormsg = errormsg;
  }

  // Determine Browser
  var browser = "Unknown";
  if (1 === 1 * 1) {
    // @ts-ignore - InstallTrigger is a Firefox-specific global
    var isFirefox = typeof (window as any).InstallTrigger !== "undefined";
    var isSafari =
      /constructor/i.test(window.HTMLElement.toString()) ||
      (function (p) {
        return p.toString() === "[object SafariRemoteNotification]";
      })(!(window as any)["safari"] || (typeof (window as any).safari !== "undefined" && (window as any)["safari"].pushNotification));
    // @ts-ignore - documentMode is IE-specific
    var isIE = /*@cc_on!@*/ false || !!(document as any).documentMode;
    // @ts-ignore - StyleMedia is Edge-specific
    var isEdge = !isIE && !!(window as any).StyleMedia;
    // @ts-ignore - chrome is Chrome-specific
    var isChrome = (window as any).chrome;
    var isEdgeChromium = isChrome && navigator.userAgent.indexOf("Edg") !== -1;
    var isBlink = isChrome && !!window.CSS;

    if (isFirefox) browser = "Firefox";
    else if (isSafari) browser = "Safari";
    else if (isIE) browser = "IE";
    else if (isEdge) browser = "Edge";
    else if (isEdgeChromium) browser = "Edge Chromium";
    else if (isChrome) browser = "Chrome";
    else if (isBlink) browser = "Blink";
    else browser = "Other";
  }

  // PWA Detection
  const isPWA = () => {
    // Check if running in standalone mode (PWA)
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      return 1;
    }
    // Check if running in fullscreen mode (PWA on some devices)
    if ((navigator as any).standalone === true) {
      return 1;
    }
    // Check if the app is installed via manifest
    if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
      return 1;
    }
    return 0;
  };

  // Device Type Detection
  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipad|phone|blackberry|opera mini|iemobile/i.test(userAgent);
    const isTablet = /tablet|ipad|playbook|silk/i.test(userAgent);
    
    if (isMobile && !isTablet) return 'mobile';
    if (isTablet) return 'tablet';
    return 'desktop';
  };

  // Bot Detection
  const detectBot = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const botPatterns = [
      { pattern: /googlebot/i, type: 'Google Bot' },
      { pattern: /bingbot/i, type: 'Bing Bot' },
      { pattern: /slurp/i, type: 'Yahoo Bot' },
      { pattern: /duckduckbot/i, type: 'DuckDuckGo Bot' },
      { pattern: /baiduspider/i, type: 'Baidu Bot' },
      { pattern: /yandexbot/i, type: 'Yandex Bot' },
      { pattern: /facebookexternalhit/i, type: 'Facebook Bot' },
      { pattern: /twitterbot/i, type: 'Twitter Bot' },
      { pattern: /linkedinbot/i, type: 'LinkedIn Bot' },
      { pattern: /whatsapp/i, type: 'WhatsApp Bot' },
      { pattern: /telegrambot/i, type: 'Telegram Bot' },
      { pattern: /discordbot/i, type: 'Discord Bot' },
      { pattern: /slackbot/i, type: 'Slack Bot' },
      { pattern: /redditbot/i, type: 'Reddit Bot' },
      { pattern: /pinterest/i, type: 'Pinterest Bot' },
      { pattern: /instagram/i, type: 'Instagram Bot' },
      { pattern: /tiktok/i, type: 'TikTok Bot' },
      { pattern: /snapchat/i, type: 'Snapchat Bot' },
      { pattern: /headless/i, type: 'Headless Browser' },
      { pattern: /phantomjs/i, type: 'PhantomJS' },
      { pattern: /selenium/i, type: 'Selenium' },
      { pattern: /webdriver/i, type: 'WebDriver' },
      { pattern: /crawler/i, type: 'Generic Crawler' },
      { pattern: /spider/i, type: 'Generic Spider' },
      { pattern: /bot/i, type: 'Generic Bot' }
    ];

    for (const bot of botPatterns) {
      if (bot.pattern.test(userAgent)) {
        return { isBot: 1, botType: bot.type };
      }
    }
    
    return { isBot: 0, botType: '' };
  };

  // Enhanced Error Analysis for Financial Calculators
  const analyzeError = (errorMsg: string) => {
    const errorMsgLower = errorMsg.toLowerCase();
    
    // Fatal error patterns (likely to break user experience)
    const fatalPatterns = [
      /uncaught referenceerror/i,
      /uncaught typeerror/i,
      /uncaught syntaxerror/i,
      /cannot read property/i,
      /is not a function/i,
      /is not defined/i,
      /failed to fetch/i,
      /network error/i,
      /timeout/i,
      /cors error/i,
      /script error/i,
      /parsing error/i,
      /module not found/i,
      /component is not defined/i,
      /react error/i,
      /rendering error/i,
      /calculation error/i,
      /invalid input/i,
      /division by zero/i,
      /overflow error/i
    ];
    
    // Non-fatal error patterns (annoying but not breaking)
    const nonFatalPatterns = [
      /console\.warn/i,
      /console\.log/i,
      /deprecated/i,
      /warning/i,
      /minor error/i,
      /non-critical/i,
      /performance warning/i,
      /memory leak/i,
      /slow performance/i,
      /validation warning/i,
      /format warning/i
    ];
    
    // Check if it's a fatal error
    for (const pattern of fatalPatterns) {
      if (pattern.test(errorMsgLower)) {
        return { severity: 'fatal', category: 'fatal_error' };
      }
    }
    
    // Check if it's a non-fatal error
    for (const pattern of nonFatalPatterns) {
      if (pattern.test(errorMsgLower)) {
        return { severity: 'non-fatal', category: 'warning' };
      }
    }
    
    // Default to non-fatal if we can't determine
    return { severity: 'non-fatal', category: 'unknown' };
  };

  // Track user behavior after errors
  const trackErrorBehavior = (errorMsg: string, errorSeverity: string) => {
    // Store error info for later analysis
    if (!window.TylerState.errorTracking) {
      window.TylerState.errorTracking = {
        errors: [],
        lastErrorTime: 0,
        userActionsAfterError: []
      };
    }
    
    window.TylerState.errorTracking.errors.push({
      message: errorMsg,
      severity: errorSeverity,
      category: errorCategory,
      timestamp: Date.now(),
      url: window.location.pathname,
      userAgent: navigator.userAgent
    });
    
    // Track if user continues or leaves after error
    setTimeout(() => {
      if (window.TylerState.errorTracking) {
        window.TylerState.errorTracking.userActionsAfterError.push({
          errorTime: Date.now() - 5000, // 5 seconds after error
          userStillActive: true,
          actionsTaken: 'continued_using_site'
        });
      }
    }, 5000);
    
    // Track if user leaves within 30 seconds of error
    setTimeout(() => {
      if (window.TylerState.errorTracking) {
        window.TylerState.errorTracking.userActionsAfterError.push({
          errorTime: Date.now() - 30000, // 30 seconds after error
          userStillActive: document.visibilityState === 'visible',
          actionsTaken: document.visibilityState === 'visible' ? 'stayed' : 'left_site'
        });
      }
    }, 30000);
  };

  // Get referrer information
  const getReferrer = () => {
    if (document.referrer) {
      return document.referrer;
    }
    return '';
  };

  // Get current calculator type
  const getCalculatorType = () => {
    const path = window.location.pathname;
    if (path.includes('/car')) return 'car_calculator';
    if (path.includes('/housing')) return 'housing_calculator';
    if (path.includes('/retirement')) return 'retirement_calculator';
    if (path.includes('/quickstart')) return 'quickstart_calculator';
    return 'other';
  };

  // Get user authentication status
  const getUserStatus = () => {
    // Check if user is logged in (you can adapt this to your auth system)
    const token = localStorage.getItem('auth_token');
    if (token) return 'authenticated';
    return 'guest';
  };

  // No Passwords
  if (input === "inputPassword") return 1;
  if (input === "inputPassword2") return 1;
  
  const pwaStatus = isPWA();
  const deviceType = getDeviceType();
  const botInfo = detectBot();
  const referrer = getReferrer();
  const calculatorType = getCalculatorType();
  const userStatus = getUserStatus();
  
  // Enhanced error analysis
  const errorAnalysis = analyzeError(errormsg);
  const errorSeverity = errorAnalysis.severity;
  const errorCategory = errorAnalysis.category;
  
  // Track error behavior if there's an error
  if (errormsg && errormsg !== '') {
    trackErrorBehavior(errormsg, errorSeverity);
  }
  
  var formData = new FormData();
  formData.append("section", "analytics");
  formData.append("request", "track");
  formData.append("button", button);
  formData.append("input", input);
  formData.append("value", value);
  formData.append("errormsg", errormsg);
  formData.append("html", window.location.pathname);
  formData.append("browser", browser);
  formData.append("height", window.innerHeight.toString());
  formData.append("width", window.innerWidth.toString());
  formData.append("is_pwa", pwaStatus.toString());
  formData.append("device_type", deviceType);
  formData.append("referrer", referrer);
  formData.append("user_agent", navigator.userAgent);
  formData.append("is_bot", botInfo.isBot.toString());
  formData.append("bot_type", botInfo.botType);
  formData.append("error_severity", errorSeverity);
  formData.append("error_category", errorCategory);
  formData.append("calculator_type", calculatorType);
  formData.append("user_status", userStatus);

  if (window.TylerState.testing === "on") {
    console.log("FETCH");
    console.log("section:analytics");
    console.log("request:track");
    console.log("button:" + button);
    console.log("input:" + input);
    console.log("value:" + value);
    console.log("errormsg:" + errormsg);
    console.log("html:" + window.location.pathname);
    console.log("browser:" + browser);
    console.log("height:" + window.innerHeight);
    console.log("width:" + window.innerWidth);
    console.log("is_pwa:" + pwaStatus);
    console.log("device_type:" + deviceType);
    console.log("referrer:" + referrer);
    console.log("is_bot:" + botInfo.isBot);
    console.log("bot_type:" + botInfo.botType);
    console.log("calculator_type:" + calculatorType);
    console.log("user_status:" + userStatus);
  }

  // Send to backend analytics endpoint
  const apiBase = (() => {
    const envBase = process.env.REACT_APP_API_URL;
    if (envBase) {
      // If explicitly provided, use as-is (assumed full base), caller can include /backend
      return envBase;
    }
    // Default to live backend so localhost dev doesn’t fail when no local API is running
    return 'https://larryshelp.org/backend';
  })();

  fetch(`${apiBase}/analytics/track`, {
    method: "POST",
    body: formData
  }).catch(error => {
    // Silently fail if analytics endpoint is not available
    console.log('Analytics tracking failed:', error);
  });
};

// Global error handler
if (typeof window !== 'undefined') {
  window.onerror = function (message, source, lineno, colno, error) {
    tracker("", "", "", message?.toString() || 'Unknown error');
  };

  // React error boundary fallback
  window.addEventListener('unhandledrejection', function(event) {
    tracker("", "", "", event.reason?.toString() || 'Unhandled promise rejection');
  });
}

export default tracker; 